import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Send, Hash, Plus, Loader2, Reply, X, SmilePlus, ClipboardList, Crown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, getShortName } from "@/components/shared";
import { API, isDemoMode } from "@/utils/constants";

const ChannelButton = ({ channel, active, onClick }) => (
  <button onClick={onClick} data-testid={`chat-channel-${channel.id}`}
    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${active ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}>
    {channel.type === 'team' ? <Hash className="h-4 w-4 shrink-0 opacity-60" /> : <Avatar name={channel.name || 'DM'} size="sm" />}
    <span className="truncate flex-1">{channel.name}</span>
    {channel.unread > 0 && <Badge className="bg-primary text-primary-foreground text-[10px] h-5 min-w-[20px] justify-center px-1.5">{channel.unread}</Badge>}
  </button>
);

const REACTION_EMOJIS = ["👍", "❤️", "😂", "🎉", "👀"];

export default function ChatView({ user, members, discussTask, onDiscussConsumed, onOpenTask, isPro, onShowUpgrade }) {
  const [channels, setChannels] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [dmTarget, setDmTarget] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [taskRef, setTaskRef] = useState(null);
  const [pickerFor, setPickerFor] = useState(null);
  const [quota, setQuota] = useState(null);
  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = activeId;

  const myId = isDemoMode ? "demo-user" : (user?.user_id || "demo-user");

  const fetchChannels = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chat/channels`);
      setChannels(r.data);
      return r.data;
    } catch { setChannels([]); return []; }
  }, []);

  const fetchMessages = useCallback(async (channelId) => {
    try {
      const r = await axios.get(`${API}/chat/channels/${channelId}/messages`);
      setMessages(r.data);
      axios.post(`${API}/chat/channels/${channelId}/read`).catch(() => {});
    } catch {}
  }, []);

  const fetchQuota = useCallback(async (channelId) => {
    if (isPro) { setQuota({ is_pro: true, remaining: null }); return; }
    try {
      const r = await axios.get(`${API}/chat/channels/${channelId}/quota`);
      setQuota(r.data);
    } catch {}
  }, [isPro]);

  useEffect(() => {
    fetchChannels().then(chs => {
      if (chs.length === 0) return;
      if (discussTask?.team_id) {
        const teamCh = chs.find(c => c.type === 'team' && c.team_id === discussTask.team_id);
        if (teamCh) { setActiveId(teamCh.id); setTaskRef({ task_id: discussTask.task_id, task_title: discussTask.task_title }); onDiscussConsumed?.(); return; }
      }
      if (discussTask) { setTaskRef({ task_id: discussTask.task_id, task_title: discussTask.task_title }); onDiscussConsumed?.(); }
      setActiveId(prev => prev || chs[0].id);
    });
  }, [fetchChannels]);

  useEffect(() => {
    if (!activeId) return;
    fetchMessages(activeId);
    fetchQuota(activeId);
    const iv = setInterval(() => { fetchMessages(activeRef.current); fetchChannels(); }, 4000);
    return () => clearInterval(iv);
  }, [activeId, fetchMessages, fetchChannels, fetchQuota]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const openChannel = (id) => {
    if (id === activeId) return;
    setActiveId(id);
    setMessages([]);
    setChannels(prev => prev?.map(c => c.id === id ? { ...c, unread: 0 } : c));
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || !activeId) return;
    setSending(true);
    setText("");
    try {
      await axios.post(`${API}/chat/channels/${activeId}/messages`, {
        text: t,
        reply_to: replyTo ? { id: replyTo.id, user_name: replyTo.user_name, text: replyTo.text.slice(0, 120) } : null,
        task_ref: taskRef,
      });
      setReplyTo(null);
      setTaskRef(null);
      await fetchMessages(activeId);
      fetchChannels();
      if (!isPro) fetchQuota(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
      setText(t);
      if (err.response?.status === 403 && !isPro) fetchQuota(activeId);
    }
    setSending(false);
  };

  const toggleReaction = async (messageId, emoji) => {
    setPickerFor(null);
    try {
      const r = await axios.post(`${API}/chat/messages/${messageId}/react`, { emoji });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: r.data.reactions } : m));
    } catch { toast.error("Failed"); }
  };

  const startDm = async () => {
    if (!dmTarget) return;
    try {
      const r = await axios.post(`${API}/chat/dm`, { user_id: dmTarget });
      setDmTarget("");
      await fetchChannels();
      openChannel(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (channels === null) return <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const active = channels.find(c => c.id === activeId);
  const teamChannels = channels.filter(c => c.type === 'team');
  const dmChannels = channels.filter(c => c.type === 'dm');
  const dmCandidates = (members || []).filter(m => m.id !== myId);

  return (
    <div className="flex h-[calc(100vh-73px)]" data-testid="chat-view">
      <div className="w-60 sm:w-72 border-r bg-card/50 flex flex-col shrink-0">
        <div className="p-3 space-y-4 overflow-y-auto flex-1">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 mb-1.5">Team channels</p>
            {teamChannels.length === 0 && <p className="text-xs text-muted-foreground px-2">Join a team to get a channel</p>}
            <div className="space-y-0.5">{teamChannels.map(c => <ChannelButton key={c.id} channel={c} active={c.id === activeId} onClick={() => openChannel(c.id)} />)}</div>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 mb-1.5">Direct messages</p>
            <div className="space-y-0.5">{dmChannels.map(c => <ChannelButton key={c.id} channel={c} active={c.id === activeId} onClick={() => openChannel(c.id)} />)}</div>
            {dmCandidates.length > 0 && (
              <div className="flex gap-1.5 mt-2 px-1">
                <Select value={dmTarget} onValueChange={setDmTarget}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="chat-dm-select"><SelectValue placeholder="New DM…" /></SelectTrigger>
                  <SelectContent>
                    {dmCandidates.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="icon" variant="outline" className="h-8 w-8 shrink-0" disabled={!dmTarget} onClick={startDm} data-testid="chat-dm-start-btn"><Plus className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        {active ? (
          <>
            <div className="px-4 py-3 border-b flex items-center gap-2 shrink-0">
              {active.type === 'team' ? <Hash className="h-4 w-4 text-muted-foreground" /> : <Avatar name={active.name} size="sm" />}
              <span className="font-semibold text-sm">{active.name}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">No messages yet — say hello 👋</p>}
              {messages.map(m => {
                const mine = m.user_id === myId;
                const reactions = m.reactions || {};
                return (
                  <div key={m.id} className={`group flex gap-2 ${mine ? 'flex-row-reverse' : ''}`} data-testid={`chat-msg-${m.id}`}>
                    {!mine && <Avatar name={m.user_name} size="sm" />}
                    <div className={`max-w-[70%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
                      <div className={`rounded-2xl px-3.5 py-2 ${mine ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted rounded-bl-md'}`}>
                        {!mine && <p className="text-[11px] font-semibold opacity-70 mb-0.5">{getShortName(m.user_name)}</p>}
                        {m.reply_to && (
                          <div className={`text-xs border-l-2 pl-2 mb-1.5 opacity-75 ${mine ? 'border-primary-foreground/40' : 'border-primary/40'}`}>
                            <span className="font-semibold">{getShortName(m.reply_to.user_name)}</span>: {m.reply_to.text}
                          </div>
                        )}
                        {m.task_ref && (
                          <button type="button" onClick={() => onOpenTask?.(m.task_ref.task_id)}
                            className={`flex items-center gap-1.5 text-xs rounded-lg px-2 py-1 mb-1.5 transition-colors ${mine ? 'bg-primary-foreground/15 hover:bg-primary-foreground/25' : 'bg-primary/10 text-primary hover:bg-primary/20'}`}
                            data-testid={`chat-task-ref-${m.id}`}>
                            <ClipboardList className="h-3 w-3" />{m.task_ref.task_title}
                          </button>
                        )}
                        <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-[10px] mt-0.5 ${mine ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      {Object.keys(reactions).length > 0 && (
                        <div className={`flex gap-1 mt-1 flex-wrap ${mine ? 'justify-end' : ''}`}>
                          {Object.entries(reactions).map(([emoji, uids]) => (
                            <button key={emoji} type="button" onClick={() => toggleReaction(m.id, emoji)}
                              className={`text-xs rounded-full px-1.5 py-0.5 border transition-colors ${uids.includes(myId) ? 'bg-primary/10 border-primary/40' : 'bg-muted border-transparent hover:border-border'}`}
                              data-testid={`reaction-${m.id}-${emoji}`}>
                              {emoji} {uids.length}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={`relative self-center opacity-0 group-hover:opacity-100 transition-opacity flex ${mine ? 'flex-row-reverse' : ''} gap-0.5`}>
                      <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground" title="React" onClick={() => setPickerFor(pickerFor === m.id ? null : m.id)} data-testid={`react-btn-${m.id}`}><SmilePlus className="h-3.5 w-3.5" /></button>
                      <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground" title="Reply" onClick={() => setReplyTo(m)} data-testid={`reply-btn-${m.id}`}><Reply className="h-3.5 w-3.5" /></button>
                      {pickerFor === m.id && (
                        <div className={`absolute bottom-7 ${mine ? 'right-0' : 'left-0'} bg-card border rounded-full shadow-lg px-2 py-1 flex gap-1 z-10`}>
                          {REACTION_EMOJIS.map(em => <button key={em} type="button" className="hover:scale-125 transition-transform text-base" onClick={() => toggleReaction(m.id, em)}>{em}</button>)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            {(replyTo || taskRef) && (
              <div className="px-3 pt-2 border-t flex items-center gap-2 text-xs text-muted-foreground">
                {replyTo && <span className="flex items-center gap-1.5 bg-muted rounded-lg px-2 py-1"><Reply className="h-3 w-3" />Replying to <b>{getShortName(replyTo.user_name)}</b>: {replyTo.text.slice(0, 50)}</span>}
                {taskRef && <span className="flex items-center gap-1.5 bg-primary/10 text-primary rounded-lg px-2 py-1" data-testid="composer-task-ref"><ClipboardList className="h-3 w-3" />{taskRef.task_title}</span>}
                <button type="button" onClick={() => { setReplyTo(null); setTaskRef(null); }} className="p-0.5 rounded hover:bg-muted" data-testid="composer-clear-context"><X className="h-3.5 w-3.5" /></button>
              </div>
            )}
            {!isPro && quota && quota.remaining > 0 && (
              <div className="px-4 py-1.5 border-t bg-amber-500/10 flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400" data-testid="chat-free-quota-banner">
                <Crown className="h-3.5 w-3.5 shrink-0" />
                <span>{quota.remaining} free {quota.remaining === 1 ? 'message' : 'messages'} left — <button type="button" onClick={onShowUpgrade} className="underline font-semibold hover:opacity-80" data-testid="chat-quota-upgrade-link">upgrade to Pro</button> for unlimited chat.</span>
              </div>
            )}
            {!isPro && quota && quota.remaining === 0 ? (
              <div className="p-4 border-t bg-gradient-to-r from-primary/10 to-purple-500/10 flex flex-col sm:flex-row items-center gap-3 shrink-0" data-testid="chat-limit-upsell">
                <div className="h-9 w-9 rounded-xl bg-primary/15 flex items-center justify-center shrink-0"><Crown className="h-5 w-5 text-primary" /></div>
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-sm font-semibold">You've used your 3 free messages</p>
                  <p className="text-xs text-muted-foreground">You can keep reading, but sending is a Pro feature. Upgrade for unlimited team chat, DMs and /task commands.</p>
                </div>
                <Button onClick={onShowUpgrade} data-testid="chat-upgrade-btn" className="shrink-0"><Crown className="h-4 w-4 mr-2" />Upgrade to Pro</Button>
              </div>
            ) : (
              <form onSubmit={sendMessage} className="p-3 border-t flex gap-2 shrink-0 lg:pr-44">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={taskRef ? `Discuss "${taskRef.task_title}"…` : `Message ${active.type === 'team' ? '#' : ''}${active.name} — or /task <title> to create a card`} className="flex-1" data-testid="chat-input" />
                <Button type="submit" disabled={sending || !text.trim()} data-testid="chat-send-btn"><Send className="h-4 w-4" /></Button>
              </form>
            )}
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <MessageSquare className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">Join a team or start a DM to begin chatting</p>
          </div>
        )}
      </div>
    </div>
  );
}
